import { Fragment, type ReactNode } from 'react';
import { priceUnitLabel } from '~/utils';
import type { ItemRow, ProductGroup } from './itemGroups';

export type ProductGroupCell = { columnKey: string; value: ReactNode };

export function ProductGroupTable({
  groups,
  columnLabels,
  renderRow,
  leadingColumnHeader,
}: Readonly<{
  groups: ProductGroup[];
  columnLabels: string[];
  renderRow: (row: ItemRow, group: ProductGroup) => ReactNode;
  leadingColumnHeader?: ReactNode;
}>) {
  const leadingColumnCount = leadingColumnHeader ? 1 : 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-neut-sec">
            {leadingColumnHeader && (
              <th className="font-normal pb-2 pr-4 whitespace-nowrap">
                {leadingColumnHeader}
              </th>
            )}
            <th className="font-normal pb-2 pr-4 whitespace-nowrap" />
            {columnLabels.map((label) => (
              <th
                key={label}
                className="font-normal pb-2 pr-4 whitespace-nowrap"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.productId}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={columnLabels.length + 1 + leadingColumnCount}
                  className="text-left font-semibold text-neut-prim pt-6 pb-1"
                >
                  {group.productName} (/
                  {priceUnitLabel(group.standardUnitsCode)})
                </th>
              </tr>
              {group.rows.map((row) => (
                <Fragment key={row.platformProductLengthId}>
                  {renderRow(row, group)}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProductGroupCells({
  label,
  values,
  leading,
}: Readonly<{
  label: string;
  values: ProductGroupCell[];
  leading?: ReactNode;
}>) {
  return (
    <tr>
      {leading && <td className="pr-4 py-1">{leading}</td>}
      <th
        scope="row"
        className="text-left font-normal pr-4 py-1 text-neut-sec whitespace-nowrap"
      >
        {label}
      </th>
      {values.map((cell) => (
        <td
          key={cell.columnKey}
          className="pr-4 py-1 text-neut-prim whitespace-nowrap"
        >
          {cell.value}
        </td>
      ))}
    </tr>
  );
}
